import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline, CircleMarker } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.heat';
import { hexbin as d3Hexbin } from 'd3-hexbin';

// Tile provider configurations
const TILE_PROVIDERS = {
  osm_online: {
    name: 'OpenStreetMap',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  },
  local: {
    name: 'Local TileServer',
    url: process.env.REACT_APP_LOCAL_TILE_URL || 'http://localhost:8081/styles/basic/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  },
  osm_humanitarian: {
    name: 'Humanitarian',
    url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Tiles style by <a href="https://www.hotosm.org/">HOT</a>'
  },
  carto_light: {
    name: 'CartoDB Light',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
  },
  carto_dark: {
    name: 'CartoDB Dark',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
  }
};

// Route colors for different canvassers
const ROUTE_COLORS = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
];

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Color schemes for different visualization modes
const colorSchemes = {
  party: {
    R: '#DC2626', // Republican - Red
    D: '#2563EB', // Democrat - Blue
    I: '#7C3AED', // Independent - Purple
    L: '#F59E0B', // Libertarian - Gold
    G: '#10B981', // Green - Green
    default: '#6B7280' // Other - Gray
  },
  sex: {
    M: '#3B82F6', // Male - Blue
    F: '#EC4899', // Female - Pink
    default: '#6B7280'
  },
  age: {
    '18-25': '#10B981', // Young - Green
    '26-35': '#3B82F6', // Young Adult - Blue
    '36-50': '#F59E0B', // Middle Age - Yellow
    '51-65': '#F97316', // Senior - Orange
    '65+': '#EF4444',   // Elder - Red
    default: '#6B7280'
  }
};

// Create custom colored marker icon
const createColoredIcon = (color, size = 24, opacity = 1) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      opacity: ${opacity};
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
};

// Create numbered marker icon for route stops
const createNumberedIcon = (number, color, size = 28) => {
  return L.divIcon({
    className: 'numbered-marker',
    html: `<div style="
      background-color: ${color};
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 11px;
    ">${number}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
};

// Get age range from age number
const getAgeRange = (age) => {
  if (age >= 18 && age <= 25) return '18-25';
  if (age >= 26 && age <= 35) return '26-35';
  if (age >= 36 && age <= 50) return '36-50';
  if (age >= 51 && age <= 65) return '51-65';
  if (age > 65) return '65+';
  return 'default';
};

// Get field value - handles case insensitivity
const getFieldValue = (voter, field) => {
  // Try exact match first, then case-insensitive
  if (voter[field] !== undefined) return voter[field];

  // Try capitalized version
  const capitalized = field.charAt(0).toUpperCase() + field.slice(1);
  if (voter[capitalized] !== undefined) return voter[capitalized];

  // Try uppercase
  if (voter[field.toUpperCase()] !== undefined) return voter[field.toUpperCase()];

  return undefined;
};


// Map bounds updater component
function MapBoundsUpdater({ voters }) {
  const map = useMap();

  useEffect(() => {
    if (voters && voters.length > 0) {
      const validVoters = voters.filter(v => v.lat && v.lng);
      if (validVoters.length > 0) {
        const bounds = L.latLngBounds(validVoters.map(v => [v.lat, v.lng]));
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [voters, map]);

  return null;
}

// Heatmap layer component using leaflet.heat
function HeatmapLayer({ points, radius = 25, blur = 15, maxZoom = 17, gradient = null }) {
  const map = useMap();
  const heatLayerRef = useRef(null);

  useEffect(() => {
    if (!map || points.length === 0) return;

    // Remove existing heatmap layer
    if (heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current);
    }

    // Create heatmap layer with points [lat, lng, intensity]
    const options = {
      radius,
      blur,
      maxZoom,
    };

    if (gradient) {
      options.gradient = gradient;
    }

    heatLayerRef.current = L.heatLayer(points, options);
    heatLayerRef.current.addTo(map);

    return () => {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
      }
    };
  }, [map, points, radius, blur, maxZoom, gradient]);

  return null;
}

// Hexbin layer component using d3-hexbin
function HexbinLayer({
  voters,
  radius = 50,
  colorField = null,
  colorScheme = null,
  opacity = 0.7,
  primaryColor = '#3B82F6'
}) {
  const map = useMap();
  const layerRef = useRef(null);
  const [zoomVersion, setZoomVersion] = useState(0);

  // Helper to generate hexagon vertices manually (flat-topped hexagon)
  const getHexagonVertices = (centerX, centerY, size) => {
    const vertices = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 6; // Start from top-right, flat-topped
      vertices.push([
        centerX + size * Math.cos(angle),
        centerY + size * Math.sin(angle)
      ]);
    }
    return vertices;
  };

  useEffect(() => {
    if (!map || voters.length === 0) return;

    // Remove existing layer
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
    }

    // Convert lat/lng to pixel coordinates at current zoom
    const points = voters
      .filter(v => v.lat && v.lng)
      .map(v => {
        const point = map.latLngToLayerPoint([v.lat, v.lng]);
        return { x: point.x, y: point.y, voter: v };
      });

    if (points.length === 0) return;

    // Create hexbin generator
    const hexbinGenerator = d3Hexbin()
      .radius(radius)
      .x(d => d.x)
      .y(d => d.y);

    const hexbins = hexbinGenerator(points);

    if (hexbins.length === 0) return;

    // Create a layer group for hexagons
    layerRef.current = L.layerGroup();

    // Calculate max count for color scaling
    const maxCount = Math.max(...hexbins.map(h => h.length));

    hexbins.forEach(bin => {
      // Generate hexagon vertices manually
      const pixelVertices = getHexagonVertices(bin.x, bin.y, radius);

      // Convert pixel coordinates to lat/lng
      const vertices = pixelVertices.map(([px, py]) => {
        const latLng = map.layerPointToLatLng([px, py]);
        return [latLng.lat, latLng.lng];
      });

      if (vertices.length < 3) return;

      // Determine fill color
      let fillColor = primaryColor;

      if (colorField && colorScheme) {
        // For categorical: find the mode (most common value)
        const valueCounts = {};
        bin.forEach(p => {
          const val = p.voter[colorField];
          if (val !== undefined) {
            valueCounts[val] = (valueCounts[val] || 0) + 1;
          }
        });
        const modeValue = Object.entries(valueCounts)
          .sort((a, b) => b[1] - a[1])[0]?.[0];
        if (modeValue && colorScheme[modeValue]) {
          fillColor = colorScheme[modeValue];
        }
      } else if (colorField) {
        // For numeric: calculate average and use gradient
        let sum = 0, count = 0;
        bin.forEach(p => {
          const val = p.voter[colorField];
          if (typeof val === 'number') {
            sum += val;
            count++;
          }
        });
        if (count > 0) {
          const avg = sum / count;
          // Simple gradient from light to dark based on value
          const intensity = Math.min(1, avg / 10);
          fillColor = `rgba(59, 130, 246, ${0.3 + intensity * 0.7})`;
        }
      } else {
        // Color by count density
        const intensity = bin.length / maxCount;
        fillColor = `rgba(59, 130, 246, ${0.3 + intensity * 0.7})`;
      }

      // Create polygon for hexagon
      const polygon = L.polygon(vertices, {
        fillColor,
        fillOpacity: opacity,
        color: '#fff',
        weight: 1,
        opacity: 0.8
      });

      // Add popup with stats
      let popupContent = `<div class="text-sm"><div class="font-semibold">${bin.length} voters</div>`;
      if (colorField) {
        // Calculate and show the average or mode
        let sum = 0, count = 0;
        bin.forEach(p => {
          const val = p.voter[colorField];
          if (typeof val === 'number') {
            sum += val;
            count++;
          }
        });
        if (count > 0) {
          popupContent += `<div class="text-gray-600">Avg ${colorField}: ${(sum / count).toFixed(1)}</div>`;
        }
      }
      popupContent += '</div>';
      polygon.bindPopup(popupContent);

      layerRef.current.addLayer(polygon);
    });

    layerRef.current.addTo(map);

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
      }
    };
  }, [map, voters, radius, colorField, colorScheme, opacity, primaryColor, zoomVersion]);

  // Re-render on zoom change by updating zoomVersion state
  useEffect(() => {
    const handleZoom = () => {
      setZoomVersion(v => v + 1);
    };
    map.on('zoomend', handleZoom);
    return () => map.off('zoomend', handleZoom);
  }, [map]);

  return null;
}

// Voter popup content
function VoterPopup({ voter }) {
  const partyNames = {
    R: 'Republican',
    D: 'Democrat',
    I: 'Independent',
    L: 'Libertarian',
    G: 'Green'
  };

  const partyColors = {
    R: 'bg-red-100 text-red-700',
    D: 'bg-blue-100 text-blue-700',
    I: 'bg-purple-100 text-purple-700',
    L: 'bg-yellow-100 text-yellow-700',
    G: 'bg-green-100 text-green-700'
  };

  return (
    <div className="min-w-[200px]">
      <div className="font-semibold text-gray-900">{voter.name}</div>
      <div className="text-sm text-gray-600 mt-1">
        <div>{voter.address}</div>
        {voter.phone && <div>{voter.phone}</div>}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${partyColors[voter.party] || 'bg-gray-100 text-gray-700'
          }`}>
          {partyNames[voter.party] || voter.party || 'Unknown'}
        </span>
        {voter.age && (
          <span className="text-xs text-gray-500">Age: {voter.age}</span>
        )}
        {voter.sex && (
          <span className="text-xs text-gray-500">{voter.sex === 'M' ? 'Male' : 'Female'}</span>
        )}
      </div>
      {voter.previousVotes !== undefined && (
        <div className="text-xs text-gray-500 mt-1">
          Previous Votes: {voter.previousVotes}
        </div>
      )}
      {voter.notes && (
        <div className="text-xs text-gray-500 mt-1 italic">
          {voter.notes}
        </div>
      )}
    </div>
  );
}

export default function VoterMapComponent({
  voters = [],
  colorBy = 'party',
  colorScheme = null,  // Custom color scheme from LLM visualization
  showClusters = true,
  center = [39.8283, -98.5795], // Center of USA
  zoom = 4,
  onVoterClick,
  tileProvider = 'osm_online',
  routes = [], // Array of route objects: { id, polyline: [[lat,lng],...], voters: [...], color }
  showRoutes = false,
  highlightedVoterIds = [], // IDs of voters to highlight
  dimNonHighlighted = false,
  // Cluster visualization configuration from LLM
  clusterConfig = {
    colorField: null,        // Field to use for pie chart (e.g., "party", "sex")
    colorScheme: null,       // Colors for each value
    showComposition: true,   // Show pie chart
    sizeField: null,         // Field to scale size by
    highlightRatio: true     // Show highlighted vs non-highlighted ratio
  },
  // Heatmap layer configuration from LLM
  heatmapConfig = {
    show: false,             // Whether to show heatmap layer
    field: null,             // Field to weight intensity by (null = equal weight)
    color: '#3B82F6',        // Primary color for gradient
    radius: 25,              // Heatmap point radius
    blur: 15,                // Blur amount
    opacity: 0.6             // Layer opacity
  },
  // Bubble/proportional symbols configuration from LLM
  bubbleConfig = {
    show: false,             // Whether to show bubbles instead of markers
    sizeField: null,         // Numeric field to scale bubble size by
    minRadius: 8,            // Minimum bubble radius
    maxRadius: 40,           // Maximum bubble radius
    opacity: 0.7             // Bubble opacity
  },
  // Hexbin aggregation configuration from LLM
  hexbinConfig = {
    show: false,             // Whether to show hexbin aggregation
    radius: 50,              // Hexagon radius in pixels
    colorField: null,        // Field to color hexbins by
    colorScheme: null,       // Color scheme for hexbin values
    opacity: 0.7             // Hexbin opacity
  }
}) {
  const mapRef = useRef(null);
  const [tileError, setTileError] = useState(false);

  // Get current tile provider config with fallback to online OSM if local fails
  const currentTileProvider = useMemo(() => {
    if (tileError && tileProvider === 'local') {
      return TILE_PROVIDERS.osm_online;
    }
    return TILE_PROVIDERS[tileProvider] || TILE_PROVIDERS.osm_online;
  }, [tileProvider, tileError]);

  // Create a map of lat/lng to voter data for cluster icon access
  const voterByPosition = useMemo(() => {
    const map = {};
    voters.forEach(v => {
      if (v.lat && v.lng) {
        // Use a key that can be matched from marker position
        const key = `${v.lat.toFixed(6)},${v.lng.toFixed(6)}`;
        map[key] = v;
      }
    });
    return map;
  }, [voters]);

  // Get the effective color scheme - prefer passed prop over internal defaults
  const effectiveColorScheme = useMemo(() => {
    if (colorScheme) return colorScheme;
    return colorSchemes[colorBy] || colorSchemes.party;
  }, [colorScheme, colorBy]);

  // Memoize markers to prevent unnecessary re-renders
  // When not clustering, offset markers at the same location so they're all visible
  const markers = useMemo(() => {
    // Inline the color logic to avoid dependency issues
    const getColor = (voter) => {
      const value = getFieldValue(voter, colorBy);
      if (value === undefined) return effectiveColorScheme.default || '#6B7280';
      if (colorBy === 'age' && typeof value === 'number') {
        const range = getAgeRange(value);
        return effectiveColorScheme[range] || effectiveColorScheme.default || '#6B7280';
      }
      return effectiveColorScheme[value] || effectiveColorScheme.default || '#6B7280';
    };

    // Track how many markers are at each location for offset calculation
    const locationCounts = {};
    const validVoters = voters.filter(v => v.lat && v.lng);

    // First pass: count voters at each location
    validVoters.forEach(voter => {
      const key = `${voter.lat.toFixed(6)},${voter.lng.toFixed(6)}`;
      locationCounts[key] = (locationCounts[key] || 0) + 1;
    });

    // Track current index at each location for spiral offset
    const locationIndices = {};

    return validVoters.map(voter => {
      const isHighlighted = highlightedVoterIds.length === 0 || highlightedVoterIds.includes(voter.id);
      const opacity = dimNonHighlighted && !isHighlighted ? 0.3 : 1;
      const size = isHighlighted ? 24 : 18;
      const color = getColor(voter);

      // Calculate offset for overlapping markers (only when not clustering)
      const key = `${voter.lat.toFixed(6)},${voter.lng.toFixed(6)}`;
      const totalAtLocation = locationCounts[key];
      const indexAtLocation = locationIndices[key] || 0;
      locationIndices[key] = indexAtLocation + 1;

      let displayLat = voter.lat;
      let displayLng = voter.lng;

      // If multiple markers at same location and not clustering, apply spiral offset
      if (totalAtLocation > 1 && !showClusters) {
        // Spiral pattern: each marker gets offset in a different direction
        // Offset is about 10-15 meters (roughly 0.0001 degrees at mid-latitudes)
        const offsetDistance = 0.00012; // ~12 meters
        const angle = (indexAtLocation / totalAtLocation) * 2 * Math.PI;
        const spiralRadius = offsetDistance * (1 + Math.floor(indexAtLocation / 8) * 0.5); // Expand spiral for many markers
        displayLat = voter.lat + spiralRadius * Math.sin(angle);
        displayLng = voter.lng + spiralRadius * Math.cos(angle);
      }

      return {
        ...voter,
        displayLat,
        displayLng,
        color,
        opacity,
        isHighlighted,
        icon: createColoredIcon(color, size, opacity)
      };
    });
  }, [voters, colorBy, effectiveColorScheme, highlightedVoterIds, dimNonHighlighted, showClusters]);

  // Calculate heatmap points when heatmap is enabled
  const heatmapPoints = useMemo(() => {
    if (!heatmapConfig.show) return [];

    const validVoters = voters.filter(v => v.lat && v.lng);

    // If filtering is active (highlightedVoterIds), only include highlighted voters in heatmap
    const votersForHeatmap = highlightedVoterIds.length > 0
      ? validVoters.filter(v => highlightedVoterIds.includes(v.id))
      : validVoters;

    return votersForHeatmap.map(voter => {
      let intensity = 1;

      // If a weight field is specified, use it for intensity
      if (heatmapConfig.field) {
        const fieldValue = getFieldValue(voter, heatmapConfig.field);
        if (typeof fieldValue === 'number') {
          // Normalize intensity - higher values = more intensity
          intensity = Math.max(0.1, Math.min(1, fieldValue / 10));
        }
      }

      return [voter.lat, voter.lng, intensity];
    });
  }, [voters, heatmapConfig.show, heatmapConfig.field, highlightedVoterIds]);

  // Create heatmap gradient from the configured color
  const heatmapGradient = useMemo(() => {
    if (!heatmapConfig.show) return null;

    const baseColor = heatmapConfig.color || '#3B82F6';

    // Create a gradient from transparent to the configured color
    return {
      0.0: 'rgba(0, 0, 0, 0)',
      0.2: baseColor + '33',  // 20% opacity
      0.4: baseColor + '66',  // 40% opacity
      0.6: baseColor + '99',  // 60% opacity
      0.8: baseColor + 'CC',  // 80% opacity
      1.0: baseColor         // Full color
    };
  }, [heatmapConfig.show, heatmapConfig.color]);

  // Calculate bubble markers when bubbles are enabled
  const bubbleMarkers = useMemo(() => {
    if (!bubbleConfig.show || !bubbleConfig.sizeField) return [];

    const validVoters = voters.filter(v => v.lat && v.lng);

    // If filtering is active, only include highlighted voters
    const votersForBubbles = highlightedVoterIds.length > 0
      ? validVoters.filter(v => highlightedVoterIds.includes(v.id))
      : validVoters;

    // Find min/max values for scaling
    const values = votersForBubbles
      .map(v => getFieldValue(v, bubbleConfig.sizeField))
      .filter(v => typeof v === 'number');

    if (values.length === 0) return [];

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const valueRange = maxValue - minValue || 1;

    return votersForBubbles.map(voter => {
      const value = getFieldValue(voter, bubbleConfig.sizeField);
      const numValue = typeof value === 'number' ? value : minValue;

      // Scale radius between min and max
      const normalizedValue = (numValue - minValue) / valueRange;
      const radius = bubbleConfig.minRadius + normalizedValue * (bubbleConfig.maxRadius - bubbleConfig.minRadius);

      // Get color
      const colorValue = getFieldValue(voter, colorBy);
      let color = effectiveColorScheme.default || '#3B82F6';
      if (colorValue !== undefined) {
        if (colorBy === 'age' && typeof colorValue === 'number') {
          const range = getAgeRange(colorValue);
          color = effectiveColorScheme[range] || color;
        } else {
          color = effectiveColorScheme[colorValue] || color;
        }
      }

      const isHighlighted = highlightedVoterIds.length === 0 || highlightedVoterIds.includes(voter.id);
      const opacity = dimNonHighlighted && !isHighlighted ? 0.3 : bubbleConfig.opacity;

      return {
        ...voter,
        radius,
        color,
        opacity,
        sizeValue: numValue
      };
    });
  }, [voters, bubbleConfig, colorBy, effectiveColorScheme, highlightedVoterIds, dimNonHighlighted]);

  // Create SVG pie chart for cluster
  const createPieChartSvg = (segments, size, highlightRatio = null) => {
    const radius = size / 2 - 2;
    const cx = size / 2;
    const cy = size / 2;
    let paths = '';

    // Draw base circle or pie chart segments
    if (!segments || segments.length === 0) {
      // No segments - draw a solid circle
      paths = `<circle cx="${cx}" cy="${cy}" r="${radius}" fill="rgba(59, 130, 246, 0.8)" />`;
    } else {
      const total = segments.reduce((sum, s) => sum + s.count, 0);
      if (total === 0) {
        paths = `<circle cx="${cx}" cy="${cy}" r="${radius}" fill="rgba(59, 130, 246, 0.8)" />`;
      } else {
        let currentAngle = -90; // Start from top

        segments.forEach((segment) => {
          const percentage = segment.count / total;
          const angle = percentage * 360;

          if (percentage >= 1) {
            // Full circle
            paths += `<circle cx="${cx}" cy="${cy}" r="${radius}" fill="${segment.color}" />`;
          } else if (percentage > 0) {
            const startAngle = currentAngle * Math.PI / 180;
            const endAngle = (currentAngle + angle) * Math.PI / 180;

            const x1 = cx + radius * Math.cos(startAngle);
            const y1 = cy + radius * Math.sin(startAngle);
            const x2 = cx + radius * Math.cos(endAngle);
            const y2 = cy + radius * Math.sin(endAngle);

            const largeArcFlag = angle > 180 ? 1 : 0;

            paths += `<path d="M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z" fill="${segment.color}" />`;
          }

          currentAngle += angle;
        });
      }
    }

    // Add highlight ratio ring if applicable (shows even without pie segments)
    if (highlightRatio !== null) {
      const ringRadius = radius + 4;

      if (highlightRatio >= 1) {
        // Full ring - all matching
        paths += `<circle cx="${cx}" cy="${cy}" r="${ringRadius}" fill="none" stroke="#10B981" stroke-width="3" />`;
      } else if (highlightRatio > 0) {
        const highlightAngle = highlightRatio * 360;
        const startAngle = -90 * Math.PI / 180;
        const endAngle = (-90 + highlightAngle) * Math.PI / 180;

        const x1 = cx + ringRadius * Math.cos(startAngle);
        const y1 = cy + ringRadius * Math.sin(startAngle);
        const x2 = cx + ringRadius * Math.cos(endAngle);
        const y2 = cy + ringRadius * Math.sin(endAngle);

        const largeArcFlag = highlightAngle > 180 ? 1 : 0;

        // Background ring (gray) for non-matching portion
        paths += `<circle cx="${cx}" cy="${cy}" r="${ringRadius}" fill="none" stroke="rgba(156, 163, 175, 0.5)" stroke-width="3" />`;
        // Highlight arc (green) for matching portion
        paths += `<path d="M ${x1} ${y1} A ${ringRadius} ${ringRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}" fill="none" stroke="#10B981" stroke-width="3" stroke-linecap="round" />`;
      } else {
        // No matches - show full gray ring
        paths += `<circle cx="${cx}" cy="${cy}" r="${ringRadius}" fill="none" stroke="rgba(156, 163, 175, 0.5)" stroke-width="3" />`;
      }
    }

    return paths;
  };

  // Create cluster icon with dynamic composition visualization
  const createClusterCustomIcon = (cluster) => {
    const count = cluster.getChildCount();
    const childMarkers = cluster.getAllChildMarkers();

    // Calculate base size
    let baseSize = 30;
    if (count > 100) baseSize = 50;
    else if (count > 50) baseSize = 45;
    else if (count > 20) baseSize = 40;
    else if (count > 10) baseSize = 35;

    // Determine the effective color field and scheme
    // Priority: 1) LLM-specified cluster config, 2) colorBy prop, 3) none (just show highlight ratio)
    const effectiveColorField = clusterConfig.colorField || null;

    // Get the color scheme - use LLM scheme if available, otherwise use defaults only if we have a known field
    let effectiveClusterColorScheme = null;
    if (clusterConfig.colorScheme && Object.keys(clusterConfig.colorScheme).length > 0) {
      // LLM provided a color scheme
      effectiveClusterColorScheme = clusterConfig.colorScheme;
    } else if (effectiveColorField && colorSchemes[effectiveColorField]) {
      // Fall back to internal defaults only if the field is known (party, sex, age)
      effectiveClusterColorScheme = colorSchemes[effectiveColorField];
    } else if (colorBy && colorSchemes[colorBy]) {
      // Use colorBy as last resort for known fields
      effectiveClusterColorScheme = colorSchemes[colorBy];
    }

    // If we have highlighted voters but no color scheme, just show highlight ratio
    const showPieChart = effectiveClusterColorScheme && clusterConfig.showComposition !== false;
    const colorFieldForAnalysis = effectiveColorField || colorBy;

    // Analyze cluster composition
    const composition = {};
    let highlightedCount = 0;
    let sizeFieldSum = 0;
    let sizeFieldCount = 0;

    childMarkers.forEach(marker => {
      // Look up voter by marker position
      const latlng = marker.getLatLng();
      const key = `${latlng.lat.toFixed(6)},${latlng.lng.toFixed(6)}`;
      const voter = voterByPosition[key] || {};

      // Count by color field - only if we're showing a pie chart
      if (showPieChart && colorFieldForAnalysis) {
        let value = getFieldValue(voter, colorFieldForAnalysis);
        if (value !== undefined) {
          // For numeric fields, bucket them
          if (typeof value === 'number' && colorFieldForAnalysis === 'age') {
            value = getAgeRange(value);
          }
          value = String(value);
          composition[value] = (composition[value] || 0) + 1;
        }
      }

      // Count highlighted
      if (highlightedVoterIds.length > 0 && highlightedVoterIds.includes(voter.id)) {
        highlightedCount++;
      }

      // Sum size field
      if (clusterConfig.sizeField) {
        const sizeValue = getFieldValue(voter, clusterConfig.sizeField);
        if (typeof sizeValue === 'number') {
          sizeFieldSum += sizeValue;
          sizeFieldCount++;
        }
      }
    });

    // Adjust size based on size field average
    let adjustedSize = baseSize;
    if (clusterConfig.sizeField && sizeFieldCount > 0) {
      const avgValue = sizeFieldSum / sizeFieldCount;
      // Scale size: 0-2 votes = normal, 3-4 = +20%, 5+ = +40%
      if (avgValue >= 5) adjustedSize = baseSize * 1.4;
      else if (avgValue >= 3) adjustedSize = baseSize * 1.2;
    }

    // Build pie segments only if we have a color scheme to use
    const segments = [];
    if (showPieChart && Object.keys(composition).length > 0) {
      Object.entries(composition).forEach(([value, segmentCount]) => {
        const color = effectiveClusterColorScheme[value] || effectiveClusterColorScheme.default || '#6B7280';
        segments.push({ value, count: segmentCount, color });
      });
      // Sort by count descending for better visual
      segments.sort((a, b) => b.count - a.count);
    }

    // Calculate highlight ratio
    let highlightRatio = null;
    if (clusterConfig.highlightRatio && highlightedVoterIds.length > 0) {
      highlightRatio = highlightedCount / count;
    }

    // Determine if we need extra space for highlight ring
    const svgSize = highlightRatio !== null ? adjustedSize + 10 : adjustedSize;

    // Generate SVG
    const pieChart = createPieChartSvg(segments, adjustedSize, highlightRatio);
    const offset = highlightRatio !== null ? 5 : 0;

    return L.divIcon({
      html: `
        <div style="position: relative; width: ${svgSize}px; height: ${svgSize}px;">
          <svg width="${svgSize}" height="${svgSize}" viewBox="0 0 ${svgSize} ${svgSize}">
            <g transform="translate(${offset}, ${offset})">
              ${pieChart}
              <circle cx="${adjustedSize/2}" cy="${adjustedSize/2}" r="${adjustedSize/2 - 2}" fill="none" stroke="white" stroke-width="2" />
            </g>
          </svg>
          <div style="
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            color: white;
            font-weight: bold;
            font-size: ${adjustedSize > 40 ? '14px' : '11px'};
            text-shadow: 0 1px 2px rgba(0,0,0,0.5);
            pointer-events: none;
          ">${count}</div>
        </div>
      `,
      className: 'custom-cluster-icon',
      iconSize: [svgSize, svgSize],
      iconAnchor: [svgSize / 2, svgSize / 2]
    });
  };

  // Handle tile load errors for fallback
  const handleTileError = () => {
    if (tileProvider === 'local' && !tileError) {
      console.warn('Local tile server unavailable, falling back to online OSM');
      setTileError(true);
    }
  };

  return (
    <MapContainer
      ref={mapRef}
      center={center}
      zoom={zoom}
      className="h-full w-full rounded-lg"
      style={{ minHeight: '300px' }}
    >
      <TileLayer
        attribution={currentTileProvider.attribution}
        url={currentTileProvider.url}
        eventHandlers={{
          tileerror: handleTileError
        }}
      />

      <MapBoundsUpdater voters={voters} />

      {/* Render heatmap layer underneath markers */}
      {heatmapConfig.show && heatmapPoints.length > 0 && (
        <HeatmapLayer
          points={heatmapPoints}
          radius={heatmapConfig.radius || 25}
          blur={heatmapConfig.blur || 15}
          gradient={heatmapGradient}
        />
      )}

      {/* Render route polylines */}
      {showRoutes && routes.map((route, routeIndex) => (
        <Polyline
          key={route.id || `route-${routeIndex}`}
          positions={route.polyline}
          pathOptions={{
            color: route.color || ROUTE_COLORS[routeIndex % ROUTE_COLORS.length],
            weight: 4,
            opacity: 0.8,
            dashArray: route.dashed ? '10, 10' : null
          }}
        />
      ))}

      {/* Render numbered route stop markers when routes are shown */}
      {showRoutes && routes.map((route, routeIndex) => (
        route.voters && route.voters.map((voter, stopIndex) => (
          voter.lat && voter.lng && (
            <Marker
              key={`route-${routeIndex}-stop-${stopIndex}`}
              position={[voter.lat, voter.lng]}
              icon={createNumberedIcon(
                stopIndex + 1,
                route.color || ROUTE_COLORS[routeIndex % ROUTE_COLORS.length]
              )}
              eventHandlers={{
                click: () => onVoterClick && onVoterClick(voter)
              }}
            >
              <Popup>
                <div className="text-xs text-gray-500 mb-1">
                  Stop #{stopIndex + 1} • Route {routeIndex + 1}
                </div>
                <VoterPopup voter={voter} />
              </Popup>
            </Marker>
          )
        ))
      ))}

      {/* Render hexbin layer when enabled (replaces regular markers) */}
      {!showRoutes && hexbinConfig.show && (
        <HexbinLayer
          voters={highlightedVoterIds.length > 0
            ? voters.filter(v => highlightedVoterIds.includes(v.id))
            : voters}
          radius={hexbinConfig.radius || 50}
          colorField={hexbinConfig.colorField}
          colorScheme={hexbinConfig.colorScheme}
          opacity={hexbinConfig.opacity || 0.7}
          primaryColor={colorScheme?.default || '#3B82F6'}
        />
      )}

      {/* Render bubble markers when enabled */}
      {!showRoutes && bubbleConfig.show && !hexbinConfig.show && bubbleMarkers.map((voter, index) => (
        <CircleMarker
          key={voter.id || index}
          center={[voter.lat, voter.lng]}
          radius={voter.radius}
          pathOptions={{
            fillColor: voter.color,
            fillOpacity: voter.opacity,
            color: '#fff',
            weight: 2,
            opacity: 0.8
          }}
          eventHandlers={{
            click: () => onVoterClick && onVoterClick(voter)
          }}
        >
          <Popup>
            <VoterPopup voter={voter} />
            <div className="text-xs text-gray-500 mt-1 border-t pt-1">
              {bubbleConfig.sizeField}: {voter.sizeValue}
            </div>
          </Popup>
        </CircleMarker>
      ))}

      {/* Regular voter markers (hidden when showing routes, bubbles, or hexbin) */}
      {!showRoutes && !bubbleConfig.show && !hexbinConfig.show && (showClusters ? (
        <MarkerClusterGroup
          chunkedLoading
          iconCreateFunction={createClusterCustomIcon}
          maxClusterRadius={50}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
        >
          {markers.map((voter, index) => (
            <Marker
              key={voter.id || index}
              position={[voter.lat, voter.lng]}
              icon={voter.icon}
              eventHandlers={{
                click: () => onVoterClick && onVoterClick(voter)
              }}
            >
              <Popup>
                <VoterPopup voter={voter} />
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      ) : (
        markers.map((voter, index) => (
          <Marker
            key={voter.id || index}
            position={[voter.displayLat, voter.displayLng]}
            icon={voter.icon}
            eventHandlers={{
              click: () => onVoterClick && onVoterClick(voter)
            }}
          >
            <Popup>
              <VoterPopup voter={voter} />
            </Popup>
          </Marker>
        ))
      ))}
    </MapContainer>
  );
}

// Export color schemes, tile providers, and route colors for use in other components
export { colorSchemes, getAgeRange, TILE_PROVIDERS, ROUTE_COLORS };
